/**
 * AI 对话提及（员工 / 客户）的纯逻辑：token 解析与 markdown（hast）处理。
 *
 * token 约定为 Markdown 链接，与后端 system prompt 里告知 LLM 的格式一致：
 * - 员工 `[姓名](user://userId)` → 渲染成 `@姓名`
 * - 客户 `[客户姓名](customer://customerId)` → 渲染成带客户图标的标签
 * - 部门 `[部门名](department://departmentId)` → 渲染成部门标签（仅管理层可用）
 * 发给 LLM 的消息只携带姓名 + id，手机号等敏感信息不进消息内容。
 *
 * 输入框（antd Mentions）插入员工时组件会自动补 `@` 前缀，因此用户消息里的形态是
 * `@[姓名](user://userId)`；渲染时统一吞掉这个字面 `@`，避免显示成 `@@姓名`。
 */

export type MentionKind = 'user' | 'customer' | 'department';

/** 链接协议，rehype-sanitize 需要显式放行（默认只允许 http/https/mailto 等） */
export const MENTION_PROTOCOLS: Record<MentionKind, string> = {
  user: 'user',
  customer: 'customer',
  department: 'department',
};

/** 姓名不含 ] 与换行，1-32 个字符；id 纯数字（≤18 位，Long 安全范围） */
export const MENTION_PATTERN = /@?\[([^\]\n]{1,32})\]\((user|customer|department):\/\/(\d{1,18})\)/g;

export interface MentionRef {
  kind: MentionKind;
  id: string;
}

export interface MentionSegment extends Partial<MentionRef> {
  type: 'text' | 'mention';
  /** 原文片段（mention 为完整 token，如 `@[张三](user://10023)`） */
  text: string;
  name?: string;
}

/** 输入框触发符：@ 找员工，# 找客户，~ 找部门（antd Mentions 的 prefix 数组） */
export const MENTION_PREFIX: Record<MentionKind, string> = {
  user: '@',
  customer: '#',
  department: '~',
};

export const MENTION_PREFIXES = Object.values(MENTION_PREFIX);

/** 由触发符反查提及类型；不是提及触发符时返回 null */
export function mentionKindOfPrefix(prefix: string): MentionKind | null {
  const entry = Object.entries(MENTION_PREFIX).find(([, value]) => value === prefix);
  return entry ? (entry[0] as MentionKind) : null;
}

/** 提及的 Markdown token（发送前才拼出来，输入框里不出现） */
export function buildMentionToken(name: string, id: string | number, kind: MentionKind = 'user'): string {
  return `[${name}](${MENTION_PROTOCOLS[kind]}://${id})`;
}

/** 输入框里一次 @ / # 选择的记录，按插入顺序保存 */
export interface MentionPick {
  kind: MentionKind;
  name: string;
  id: string;
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * 把输入框里的明文提及还原成后端/LLM 认识的 Markdown token：
 * - `@张三`   → `@[张三](user://10023)`（员工标签自带 @，前缀保留）
 * - `#王小明` → `[王小明](customer://881)`（客户标签不带前缀，`#` 吞掉）
 *
 * 输入框用的是 antd Mentions（底层就是 textarea，塞不进富文本），所以选中候选时
 * 只插入姓名，id 记在 picks 里，发送前在这里配对回去。规则：
 * - 只替换 picks 里出现过的姓名，用户手打的 @某某 / #某某 保持原样（当普通文本）
 * - 同名多个按插入顺序依次配对；文本里出现次数比选中次数多时（复制粘贴），
 *   多出来的沿用该姓名最后一次选中的 id
 * - 长名字优先匹配，避免「张三」把「张三丰」截断
 * - 员工与客户各算各的，同名的员工和客户不会互相抢
 */
export function encodeMentions(text: string, picks: MentionPick[]): string {
  if (!text || picks.length === 0) {
    return text;
  }
  let encoded = text;
  (Object.keys(MENTION_PREFIX) as MentionKind[]).forEach((kind) => {
    encoded = encodeKind(encoded, picks.filter((pick) => pick.kind === kind), kind);
  });
  return encoded;
}

function encodeKind(text: string, picks: MentionPick[], kind: MentionKind): string {
  if (picks.length === 0) {
    return text;
  }
  const prefix = MENTION_PREFIX[kind];
  const queues = new Map<string, string[]>();
  picks.forEach((pick) => {
    const queue = queues.get(pick.name);
    if (queue) {
      queue.push(pick.id);
    } else {
      queues.set(pick.name, [pick.id]);
    }
  });
  const names = Array.from(queues.keys()).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(
    `${escapeRegExp(prefix)}(${names.map(escapeRegExp).join('|')})`,
    'g',
  );
  return text.replace(pattern, (matched, name: string) => {
    const queue = queues.get(name);
    if (!queue || queue.length === 0) {
      return matched;
    }
    // 只剩最后一个时不再出队，供重复出现的同名提及复用
    const id = queue.length > 1 ? (queue.shift() as string) : queue[0];
    // 员工渲染成 @姓名，前缀留着；客户标签不带前缀，这里把 # 吞掉
    const keptPrefix = kind === 'user' ? prefix : '';
    return `${keptPrefix}${buildMentionToken(name, id, kind)}`;
  });
}

/** 丢掉输入框里已经被删除的提及记录，避免 picks 无限增长、错配到后来的同名文本 */
export function pruneMentionPicks(text: string, picks: MentionPick[]): MentionPick[] {
  const kept: MentionPick[] = [];
  picks.forEach((pick) => {
    const occurrences = countOccurrences(text, `${MENTION_PREFIX[pick.kind]}${pick.name}`);
    const alreadyKept = kept.filter(
      (item) => item.kind === pick.kind && item.name === pick.name,
    ).length;
    if (alreadyKept < occurrences) {
      kept.push(pick);
    }
  });
  return kept;
}

function countOccurrences(text: string, fragment: string): number {
  let count = 0;
  let index = text.indexOf(fragment);
  while (index !== -1) {
    count++;
    index = text.indexOf(fragment, index + fragment.length);
  }
  return count;
}

/** 从链接地址解析提及目标，非提及链接返回 null */
export function parseMentionHref(href?: string | null): MentionRef | null {
  if (!href) {
    return null;
  }
  const match = /^(user|customer|department):\/\/(\d{1,18})$/.exec(href);
  return match ? { kind: match[1] as MentionKind, id: match[2] } : null;
}

/** 把文本按提及 token 切段；无 token 时返回单个 text 段（空串返回空数组） */
export function splitMentions(text: string): MentionSegment[] {
  const segments: MentionSegment[] = [];
  if (!text) {
    return segments;
  }
  // 每次新建 RegExp：模块级 g 正则的 lastIndex 会跨调用串状态
  const pattern = new RegExp(MENTION_PATTERN.source, 'g');
  let lastIndex = 0;
  let match: RegExpExecArray | null = pattern.exec(text);
  while (match !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, match.index) });
    }
    segments.push({
      type: 'mention',
      text: match[0],
      name: match[1],
      kind: match[2] as MentionKind,
      id: match[3],
    });
    lastIndex = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) });
  }
  return segments;
}

interface HastNode {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
}

/**
 * rehype 插件：吞掉紧贴在员工提及链接前面的字面 `@`。
 * 用户消息里的 token 形态是 `@[姓名](user://id)`（Mentions 自动补的前缀），
 * 而链接本身会渲染成 `@姓名`，不去掉就成了 `@@姓名`。
 * 客户提及不带 `@` 前缀，因此只处理 user 协议。
 */
export function rehypeStripMentionPrefix() {
  return (tree: unknown) => stripPrefix(tree as HastNode);
}

function stripPrefix(node: HastNode) {
  if (!node || !Array.isArray(node.children)) {
    return;
  }
  const children = node.children;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (isUserMentionLink(child) && i > 0) {
      const prev = children[i - 1];
      if (prev.type === 'text' && typeof prev.value === 'string' && prev.value.endsWith('@')) {
        prev.value = prev.value.slice(0, -1);
      }
    }
    stripPrefix(child);
  }
}

function isUserMentionLink(node: HastNode): boolean {
  return (
    node.type === 'element' &&
    node.tagName === 'a' &&
    parseMentionHref(node.properties?.href as string | undefined)?.kind === 'user'
  );
}

/** 取 hast 节点下的纯文本（提及链接的展示名） */
export function hastText(node: unknown): string {
  const target = node as HastNode | undefined;
  if (!target) {
    return '';
  }
  if (target.type === 'text') {
    return target.value || '';
  }
  return (target.children || []).map(hastText).join('');
}
